/**
 * Nhận diện doanh nghiệp cho bản điện thoại.
 *
 * Lấy qua `GET /api/company-branding` — công khai, gọi được cả ở màn đăng nhập
 * khi chưa có token. Trước đây vỏ trang Blade truy vấn thẳng cơ sở dữ liệu rồi
 * nhúng vào `window.__COMPANY__`, nên kết nối trục trặc là cả ứng dụng trắng
 * trang với lỗi 500 thay vì chỉ mất phần nhận diện.
 *
 * Dải màu giữ nguyên đường cong độ sáng và độ bão hoà của bản gốc, chỉ thay góc
 * màu. Nhờ vậy đổi màu thương hiệu không làm chữ khó đọc.
 */

export interface Branding {
  name: string;
  shortName: string;
  logoUrl: string | null;
  faviconUrl: string | null;
  themeColor: string;
}

const FALLBACK: Branding = {
  name: "Mega Việt Phát",
  shortName: "MVP",
  logoUrl: null,
  faviconUrl: null,
  themeColor: "#C1272D",
};

/** Đường cong L và C của dải màu, giữ nguyên khi đổi góc màu. */
const RAMP: [stop: string, lightness: number, chroma: number][] = [
  ["50", 0.97, 0.02],
  ["100", 0.94, 0.04],
  ["200", 0.88, 0.08],
  ["300", 0.78, 0.13],
  ["400", 0.65, 0.17],
  ["500", 0.53, 0.2],
  ["600", 0.47, 0.2],
  ["700", 0.42, 0.2],
  ["800", 0.37, 0.19],
  ["900", 0.32, 0.17],
  ["950", 0.25, 0.15],
];

/*
 * Bản nhận diện đã nạp. Giữ ở mức mô-đun vì `getBranding()` được gọi đồng bộ ở
 * nhiều nơi lúc dựng giao diện, không chờ được lời gọi mạng.
 */
let cached: Branding = FALLBACK;

export function getBranding(): Branding {
  return cached;
}

/**
 * Nạp nhận diện từ máy chủ rồi áp dải màu.
 *
 * Gọi một lần lúc khởi động. Hỏng thì giữ nguyên giá trị mặc định — mất logo
 * còn hơn chặn cả ứng dụng vì một chi tiết trang trí.
 */
export async function loadBranding(): Promise<Branding> {
  try {
    const res = await fetch('/api/company-branding', { headers: { Accept: 'application/json' } });

    if (!res.ok) return cached;

    const body = (await res.json()) as { data?: Partial<Branding> };

    if (body?.data) {
      cached = { ...FALLBACK, ...body.data };
      applyBrandTheme(cached.themeColor);
      document.title = `${cached.shortName || cached.name} Kho - Nhận hàng`;
    }
  } catch {
    // Ngoại tuyến hoặc máy chủ trục trặc: dùng mặc định.
  }

  return cached;
}

function toLinear(channel: number): number {
  const c = channel / 255;
  return c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
}

/** Góc màu OKLCH của mã màu #rrggbb; null khi màu gần như xám. */
export function hexToOklchHue(hex: string): number | null {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());

  if (!m) return null;

  const int = parseInt(m[1], 16);
  const r = toLinear((int >> 16) & 255);
  const g = toLinear((int >> 8) & 255);
  const b = toLinear(int & 255);

  const l = Math.cbrt(0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b);
  const mid = Math.cbrt(0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b);
  const s = Math.cbrt(0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b);

  const a = 1.9779984951 * l - 2.428592205 * mid + 0.4505937099 * s;
  const bb = 0.0259040371 * l + 0.7827717662 * mid - 0.808675766 * s;

  if (Math.hypot(a, bb) < 0.002) return null;

  return ((Math.atan2(bb, a) * 180) / Math.PI + 360) % 360;
}

/** Ghi dải màu thương hiệu lên tài liệu. Gọi một lần khi khởi động ứng dụng. */
export function applyBrandTheme(hex?: string): void {
  const hue = hexToOklchHue(hex ?? getBranding().themeColor);

  // Màu xám thì giữ dải mặc định trong index.css.
  if (hue === null) return;

  const root = document.documentElement;
  const rounded = hue.toFixed(1);

  RAMP.forEach(([stop, lightness, chroma]) => {
    root.style.setProperty(`--brand-${stop}`, `${lightness} ${chroma} ${rounded}`);
  });
}
